import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { motion, useDragControls, useMotionValue } from 'framer-motion';
import { LocalVideoTrack, RemoteVideoTrack } from 'agora-rtc-react';
import { FiMaximize2, FiMic, FiMicOff, FiPhoneOff, FiVideo, FiVideoOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useCallSession } from './callSessionContext';
import { useCallMedia } from './callMediaEngine';
import { CallStatsChip } from './CallStatsChip';
import { getCallTiming, subscribeCallTiming } from './callTimingStore';

const OVERLAY_W = 208;
const OVERLAY_H = 156;
const EDGE_PAD = 16;

const getViewportBounds = () => ({
  left: EDGE_PAD,
  top: EDGE_PAD,
  right: window.innerWidth - OVERLAY_W - EDGE_PAD,
  bottom: window.innerHeight - OVERLAY_H - EDGE_PAD,
});

const clamp = (x, y) => {
  const b = getViewportBounds();
  return {
    x: Math.min(Math.max(x, b.left), b.right),
    y: Math.min(Math.max(y, b.top), b.bottom),
  };
};

const snapToCorner = (x, y) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const toLeft = x + OVERLAY_W / 2 < vw / 2;
  const toTop = y + OVERLAY_H / 2 < vh / 2;
  return clamp(
    toLeft ? EDGE_PAD : vw - OVERLAY_W - EDGE_PAD,
    toTop ? EDGE_PAD : vh - OVERLAY_H - EDGE_PAD,
  );
};

const CallOverlayInner = () => {
  const navigate = useNavigate();
  const { endCall, expandCall, appointmentPath } = useCallSession();
  const media = useCallMedia();
  const dragControls = useDragControls();

  const x = useMotionValue(getViewportBounds().right);
  const y = useMotionValue(getViewportBounds().bottom);
  const [bounds, setBounds] = useState(getViewportBounds);

  useEffect(() => {
    const onResize = () => {
      const newBounds = getViewportBounds();
      setBounds(newBounds);
      const next = clamp(x.get(), y.get());
      x.set(next.x);
      y.set(next.y);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [x, y]);

  const {
    micOn,
    camOn,
    isScreenSharing,
    localVideoTrack,
    remoteUser,
    remotePresent,
    micAvailable,
    camAvailable,
    setMicOn,
    setCamOn,
  } = media || {};

  const timing = useSyncExternalStore(subscribeCallTiming, getCallTiming, getCallTiming);
  const remoteHasVideo = Boolean(remotePresent && remoteUser?.hasVideo && remoteUser?.videoTrack);

  const handleExpand = useCallback(() => {
    expandCall();
    navigate('/appointment/chat');
  }, [expandCall, navigate]);

  const handleDragEnd = useCallback(() => {
    const next = snapToCorner(x.get(), y.get());
    x.set(next.x);
    y.set(next.y);
  }, [x, y]);

  const handleEndCall = useCallback(async () => {
    await endCall();
    navigate(appointmentPath || '/courses');
  }, [endCall, navigate, appointmentPath]);

  const formatCountdown = (secs) => {
    const m = Math.floor(Math.max(0, secs) / 60);
    const s = String(Math.max(0, secs) % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <motion.div
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={bounds}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999,
        width: OVERLAY_W,
        height: OVERLAY_H,
        x,
        y,
      }}
      onDragEnd={handleDragEnd}
      className="rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-slate-900 select-none"
    >
      <div className="relative w-full h-full">
        <div
          className="absolute top-0 inset-x-0 h-7 z-30 cursor-grab active:cursor-grabbing"
          onPointerDown={(e) => dragControls.start(e)}
        />

        {!timing.sessionStarted ? (
          <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center gap-1 px-3">
            <span className="text-white/70 text-xs font-medium">Waiting for course</span>
            <span className="text-white/45 text-[10px]">
              {remotePresent ? 'Both joined' : 'Waiting for peer…'}
            </span>
          </div>
        ) : remotePresent && remoteUser && remoteHasVideo ? (
          // Video-only component: never stops the engine-played remote audio.
          <RemoteVideoTrack track={remoteUser.videoTrack} play style={{ width: '100%', height: '100%' }} />
        ) : isScreenSharing && localVideoTrack ? (
          <div key="overlay-local" className="relative w-full h-full">
            <LocalVideoTrack
              track={localVideoTrack}
              play
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        ) : localVideoTrack ? (
          <div key="overlay-local" className="relative w-full h-full">
            <LocalVideoTrack
              track={localVideoTrack}
              play={camOn}
              style={{ width: '100%', height: '100%' }}
            />
            {!camOn ? (
              <div className="absolute inset-0 z-10 w-full h-full bg-slate-800 flex items-center justify-center">
                <span className="text-white/50 text-xs">
                  {remotePresent ? 'Camera off' : 'No video'}
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="w-full h-full bg-slate-800 flex items-center justify-center">
            <span className="text-white/50 text-xs">
              {remotePresent ? 'Camera off' : 'Waiting…'}
            </span>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 py-2 bg-gradient-to-t from-slate-950/80 to-transparent z-20">
          <button
            type="button"
            disabled={!micAvailable}
            onClick={() => setMicOn?.((v) => !v)}
            className="w-7 h-7 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center disabled:opacity-40"
          >
            {micOn ? <FiMic size={13} /> : <FiMicOff size={13} />}
          </button>

          <button
            type="button"
            disabled={!camAvailable || isScreenSharing}
            onClick={() => setCamOn?.((v) => !v)}
            className="w-7 h-7 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center disabled:opacity-40"
          >
            {camOn ? <FiVideo size={13} /> : <FiVideoOff size={13} />}
          </button>

          <button
            type="button"
            onClick={handleExpand}
            className="w-7 h-7 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center"
          >
            <FiMaximize2 size={13} />
          </button>

          <button
            type="button"
            onClick={handleEndCall}
            className="w-7 h-7 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center"
          >
            <FiPhoneOff size={13} />
          </button>
        </div>

        <div className="absolute top-1.5 left-2 right-2 z-20 flex items-center justify-between pointer-events-none">
          <span className="text-[10px] text-white/60 font-medium flex items-center gap-1">
            {!timing.sessionStarted ? 'Lobby' : 'Live'}
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full animate-pulse ${
                !timing.sessionStarted ? 'bg-sky-400' : 'bg-rose-500'
              }`}
            />
            <CallStatsChip compact />
          </span>
          {!timing.sessionStarted && timing.startInSeconds != null ? (
            <span className="text-[10px] text-sky-200 bg-slate-950/60 px-1.5 py-0.5 rounded tabular-nums">
              {formatCountdown(timing.startInSeconds)}
            </span>
          ) : timing.closeInSeconds != null && timing.closeInSeconds <= 60 ? (
            <span className="text-[10px] text-amber-300 bg-slate-950/60 px-1.5 py-0.5 rounded">
              {formatCountdown(timing.closeInSeconds)}
            </span>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
};

export const FloatingCallOverlay = () => {
  const { callSession } = useCallSession();
  if (!callSession?.isMinimized) return null;
  return <CallOverlayInner />;
};
